/* ============================================================
   CONTENT CONTEXT — serves published content + media to the site.
   It subscribes to Firestore and renders the in-code seed as the
   immediate fallback (and while offline / on error), so the page is
   never blank and never breaks on a missing field. Consumers
   (useContent / useMediaCss) are unchanged from Phase 1.
   ============================================================ */
import { createContext, useContext, useEffect, useState } from 'react'
import { CONTENT, MEDIA, RESOLVED_DEFAULT_SECTIONS } from './site.js'
import {
  DEFAULT_LOCALE,
  DEFAULT_SETTINGS,
  subscribeContent,
  subscribeMedia,
  subscribeSections,
  subscribeSettings,
} from './loader.js'

// Exported so the admin live-preview can supply draft content directly
// (it renders the real section components, which call useContent()).
export const ContentContext = createContext({
  content: CONTENT,
  media: MEDIA,
  sections: RESOLVED_DEFAULT_SECTIONS,
  settings: DEFAULT_SETTINGS,
})

export function ContentProvider({ children, locale = DEFAULT_LOCALE }) {
  const [content, setContent] = useState(CONTENT)
  const [media, setMedia] = useState(MEDIA)
  const [sections, setSections] = useState(RESOLVED_DEFAULT_SECTIONS)
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)

  useEffect(() => {
    const onErr = (e) => console.warn('[content] Firestore read failed; using seed.', e)
    const unsubContent = subscribeContent(locale, setContent, onErr)
    const unsubMedia = subscribeMedia(setMedia, onErr)
    const unsubSections = subscribeSections(locale, setSections, onErr)
    const unsubSettings = subscribeSettings(setSettings, onErr)
    return () => {
      unsubContent()
      unsubMedia()
      unsubSections()
      unsubSettings()
    }
  }, [locale])

  return (
    <ContentContext.Provider value={{ content, media, sections, settings }}>
      {children}
    </ContentContext.Provider>
  )
}

export function useContent() {
  return useContext(ContentContext)
}

// The resolved, ordered section list (with derived nums + hidden flags).
export function useSections() {
  return useContext(ContentContext).sections || RESOLVED_DEFAULT_SECTIONS
}

// Site-wide settings (enquiry recipient + social links).
export function useSettings() {
  return useContext(ContentContext).settings || DEFAULT_SETTINGS
}
export { DEFAULT_SETTINGS }

// Resolve a media id to a background value, with an on-brand fallback.
// Prefers a real uploaded image (url, Phase 4) over the gradient css.
export function useMediaCss(id) {
  const { media } = useContent()
  const m = media.find((x) => x.id === id)
  if (!m) return 'linear-gradient(145deg,#26221d,#131210)'
  return m.url ? `center / cover no-repeat url(${m.url})` : m.css
}
